import { useBranding } from '../branding';
import { useEffect, useState } from 'react';
import { api, type AuthCapabilities, type ClientSession } from '../api';
import { DragonflyMark } from '../components/Brand';
import { safeReturnPath } from '../../shared/return-path';

const emptyCapabilities: AuthCapabilities = {
  parentAccess: false,
  email: false,
  magicLink: false,
  passkey: false,
  security: false,
  providers: { google: false, microsoft: false, apple: false },
};

export function LoginPage({ session }: { session: ClientSession | null }) {
  const { config, preview } = useBranding();
  const [capabilities, setCapabilities] = useState<AuthCapabilities>(emptyCapabilities);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const query = new URLSearchParams(window.location.search);
  const factorRequired = query.get('factor') === 'totp';
  const callbackURL = safeReturnPath(query.get('callbackURL'));

  useEffect(() => {
    void api
      .authCapabilities()
      .then(setCapabilities)
      .catch(() => {
        if (session?.capabilities.authConfigured)
          setCapabilities((current) => ({
            ...current,
            providers: { ...current.providers, google: true },
          }));
      })
      .finally(() => setCapabilitiesLoaded(true));
  }, [session]);

  const showError = (cause: unknown, fallback: string) => {
    setError(cause instanceof Error ? cause.message : fallback);
    setBusy('');
  };
  const social = async (provider: 'google' | 'microsoft' | 'apple') => {
    setBusy(provider);
    setError('');
    try {
      sessionStorage.setItem('auth-callback-url', callbackURL);
      const { authClient } = await import('../auth-client');
      const result = await authClient.signIn.social({ provider, callbackURL });
      if (result.error) throw new Error(result.error.message || 'Sign-in could not start');
    } catch (cause) {
      showError(cause, 'Sign-in could not start');
    }
  };
  const sendCode = async () => {
    setBusy('send-code');
    setError('');
    try {
      const { authClient } = await import('../auth-client');
      const result = await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
      if (result.error) throw new Error(result.error.message || 'Could not send a code');
      setCodeSent(true);
      setBusy('');
    } catch (cause) {
      showError(cause, 'Could not send a code');
    }
  };
  const emailCodeSignIn = async () => {
    setBusy('email-code');
    setError('');
    try {
      sessionStorage.setItem('auth-callback-url', callbackURL);
      const { authClient } = await import('../auth-client');
      const result = await authClient.signIn.emailOtp({ email, otp });
      if (result.error) throw new Error(result.error.message || 'That code did not work');
      window.location.assign(callbackURL);
    } catch (cause) {
      showError(cause, 'That code did not work');
    }
  };
  const passwordSignIn = async () => {
    setBusy('password');
    setError('');
    try {
      sessionStorage.setItem('auth-callback-url', callbackURL);
      const { authClient } = await import('../auth-client');
      const result = await authClient.signIn.email({ email, password, callbackURL });
      if (result.error) throw new Error(result.error.message || 'Sign-in failed');
      window.location.assign(callbackURL);
    } catch (cause) {
      showError(cause, 'Sign-in failed');
    }
  };
  const passwordSignUp = async () => {
    setBusy('signup');
    setError('');
    try {
      const { authClient } = await import('../auth-client');
      const result = await authClient.signUp.email({ name, email, password, callbackURL });
      if (result.error) throw new Error(result.error.message || 'Account creation failed');
      setSignupComplete(true);
      setBusy('');
    } catch (cause) {
      showError(cause, 'Account creation failed');
    }
  };
  const passkeySignIn = async () => {
    setBusy('passkey');
    setError('');
    try {
      sessionStorage.setItem('auth-callback-url', callbackURL);
      const { authClient } = await import('../auth-client');
      const result = await authClient.signIn.passkey();
      if (result.error) throw new Error(result.error.message || 'Passkey sign-in failed');
      window.location.assign(callbackURL);
    } catch (cause) {
      showError(cause, 'Passkey sign-in failed');
    }
  };
  const verifyTotp = async () => {
    setBusy('totp');
    setError('');
    try {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.verifyTotp({ code: otp, trustDevice: true });
      if (result.error) throw new Error(result.error.message || 'That code did not work');
      window.location.assign(callbackURL);
    } catch (cause) {
      showError(cause, 'That code did not work');
    }
  };
  const verifyBackupCode = async () => {
    setBusy('backup-code');
    setError('');
    try {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.verifyBackupCode({ code: backupCode.trim() });
      if (result.error) throw new Error(result.error.message || 'That backup code did not work');
      window.location.assign(callbackURL);
    } catch (cause) {
      showError(cause, 'That backup code did not work');
    }
  };
  const local = async (userId: string) => {
    setBusy(userId);
    setError('');
    try {
      await api.localLogin(userId);
      window.location.assign('/');
    } catch (cause) {
      showError(cause, 'Local sign-in failed');
    }
  };

  if (session?.user && !preview)
    return (
      <main id="main-content" className="login-page">
        <div className="login-card">
          <DragonflyMark />
          <h1>You are signed in</h1>
          <p>Welcome back, {session.user.name}.</p>
          <a className="button" href="/account">
            Open your account
          </a>
        </div>
      </main>
    );

  const providerNames = Object.entries(capabilities.providers).filter(([, enabled]) => enabled) as [
    'google' | 'microsoft' | 'apple',
    boolean,
  ][];
  return (
    <main id="main-content" className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <DragonflyMark />
        <span className="eyebrow">Community account</span>
        <h1 id="login-title">{config.loginHeading}</h1>
        <p>{config.loginDescription}</p>
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
        {signupComplete && (
          <p className="inline-success" role="status">
            Check your email for a verification link, then return here to sign in.
          </p>
        )}
        {factorRequired ? (
          <div className="auth-method">
            <h2>Enter your authenticator code</h2>
            <label className="field">
              <span>Six-digit code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
              />
            </label>
            <button
              className="button"
              disabled={Boolean(busy) || !otp}
              onClick={() => void verifyTotp()}
            >
              Verify code
            </button>
            <details>
              <summary>Use a backup code instead</summary>
              <p>Each backup code can be used once.</p>
              <label className="field">
                <span>Backup code</span>
                <input
                  autoComplete="off"
                  value={backupCode}
                  onChange={(event) => setBackupCode(event.target.value)}
                />
              </label>
              <button
                className="button button-quiet"
                disabled={Boolean(busy) || !backupCode.trim()}
                onClick={() => void verifyBackupCode()}
              >
                Verify backup code
              </button>
            </details>
          </div>
        ) : (
          <>
            {providerNames.map(([provider]) => (
              <button
                key={provider}
                className="provider-button"
                disabled={Boolean(busy)}
                onClick={() => void social(provider)}
              >
                Continue with {provider.charAt(0).toUpperCase() + provider.slice(1)}
              </button>
            ))}
            {capabilities.email && (
              <div className="auth-method">
                <label className="field">
                  <span>Email address</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
                {codeSent && (
                  <label className="field">
                    <span>Email code</span>
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={otp}
                      onChange={(event) => setOtp(event.target.value)}
                    />
                  </label>
                )}
                <div className="auth-buttons">
                  <button
                    className="button button-quiet"
                    disabled={Boolean(busy) || !email}
                    onClick={() => void sendCode()}
                  >
                    {codeSent ? 'Send another code' : 'Email me a code'}
                  </button>
                  {codeSent && (
                    <button
                      className="button"
                      disabled={Boolean(busy) || !otp}
                      onClick={() => void emailCodeSignIn()}
                    >
                      Sign in with code
                    </button>
                  )}
                </div>
                <details>
                  <summary>Use a password</summary>
                  <label className="field">
                    <span>Password</span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </label>
                  <button
                    className="button"
                    disabled={Boolean(busy) || !email || !password}
                    onClick={() => void passwordSignIn()}
                  >
                    Sign in with password
                  </button>
                </details>
                {(callbackURL.startsWith('/invite?') || capabilities.publicParticipation) && (
                  <details>
                    <summary>Create an account</summary>
                    <label className="field">
                      <span>Your name</span>
                      <input
                        autoComplete="name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>Choose a password</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        minLength={15}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </label>
                    <button
                      className="button"
                      disabled={Boolean(busy) || !name || !email || password.length < 15}
                      onClick={() => void passwordSignUp()}
                    >
                      Create password account
                    </button>
                  </details>
                )}
              </div>
            )}
            {capabilities.passkey && (
              <button
                className="button button-quiet"
                disabled={Boolean(busy)}
                onClick={() => void passkeySignIn()}
              >
                Sign in with a passkey
              </button>
            )}
            {capabilitiesLoaded &&
              providerNames.length === 0 &&
              !capabilities.email &&
              !capabilities.passkey && (
                <div className="auth-unconfigured">
                  <b>Account sign-in is not configured yet</b>
                  <p>
                    The public calendar remains available while an administrator configures sign-in.
                  </p>
                </div>
              )}
          </>
        )}
        {session?.capabilities.local && (
          <div className="local-login">
            <span>Local development only</span>
            <button disabled={Boolean(busy)} onClick={() => void local('local-super-admin')}>
              Sign in as Super Admin
            </button>
            <button disabled={Boolean(busy)} onClick={() => void local('local-admin')}>
              Sign in as administrator
            </button>
            <button disabled={Boolean(busy)} onClick={() => void local('local-board')}>
              Sign in as board editor
            </button>
            <button disabled={Boolean(busy)} onClick={() => void local('local-parent')}>
              Sign in as parent
            </button>
          </div>
        )}
        <p className="login-policies">
          Read our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Use</a>.
        </p>
        <a className="back-link" href="/">
          ← Return to public calendar
        </a>
      </section>
    </main>
  );
}
